<!-- WCAG 1.3.1: Landmark region with label -->
<header class="axa-header-wrapper" role="banner" aria-label="Intestazione principale">
  <!-- Grid Layout: Logo | Navigation | Actions -->
  <div class="axa-header-grid">
    
    <!-- Logo Section - Fixed Left -->
    <div class="axa-logo-section">
      <!-- WCAG 1.1.1: Descriptive alt text for logo -->
      <img
        src="assets/images/axa-logo.svg"
        alt="AXA - Piattaforma di Allineamento Legale"
        class="axa-logo-img"
      />
      <!-- WCAG 1.3.1: App name as plain text — NOT an h1 so each page can have its own unique h1 -->
      <p class="app-title" aria-label="PalEasy - Portale AXA">{{ getAppTitle() }}</p>
    </div>

    <!-- Navigation Tabs - Desktop Only -->
    @if (!isLoginPage()) {
      <nav class="axa-nav-section desktop-only" role="navigation" aria-label="Menu di navigazione principale">
        @for (item of navItems; track item.label) {
          @if (item.children?.length) {
            <div class="nav-tab-dropdown-wrapper"
                 [class.open]="activeDropdownLabel === item.label"
                 (mouseenter)="openDropdown(item.label)"
                 (mouseleave)="closeDropdown()">
              <button
                class="nav-tab"
                [class.active]="item.active"
                [attr.aria-haspopup]="'menu'"
              >
                {{ item.label }} <i class="pi pi-angle-down text-xs ml-1" aria-hidden="true"></i>
              </button>
              <div class="nav-dropdown-panel" role="menu">
                @for (child of item.children; track child.label) {
                  <button
                    class="nav-dropdown-item"
                    (click)="navigateTo(child.route)"
                    role="menuitem"
                  >
                    @if (child.icon) { <i [class]="child.icon + ' mr-2'" aria-hidden="true"></i> }
                    {{ child.label }}
                  </button>
                }
              </div>
            </div>
          } @else {
            <button
              class="nav-tab"
              [class.active]="item.active"
              (click)="navigateTo(item.route)"
              [attr.aria-current]="item.active ? 'page' : null"
            >
              {{ item.label }}
            </button>
          }
        }
        <p-menu #navDropdownMenu [model]="activeNavChildren" [popup]="true" appendTo="body" [style]="{'display':'none'}" />
      </nav>
    }

    <!-- Actions Section - Fixed Right -->
    @if (!isLoginPage()) {
      <div class="axa-actions-section">
        <!-- Burger Menu - Mobile Only -->
        <button
          class="action-btn burger-btn mobile-only"
          (click)="toggleMobileMenu()"
          [attr.aria-expanded]="isMobileMenuOpen()"
          aria-label="Menu"
        >
          <i class="pi pi-bars"></i>
        </button>

        <!-- Alert Bell - solo per backoffice_professional e admin -->
        @if (shouldLoadAlerts()) {
          <button
            class="action-btn bell-btn"
            (click)="navigateTo('/axa/alerts')"
            aria-label="Avvisi"
            pTooltip="Vai agli avvisi"
            tooltipPosition="bottom">
            <i class="pi pi-bell"></i>
            @if (unreadAlertsCount > 0) {
              <span class="bell-badge">{{ unreadAlertsCount > 99 ? '99+' : unreadAlertsCount }}</span>
            }
          </button>
        }

        <!-- User Menu -->
        <button
          class="action-btn user-btn"
          (click)="userMenu.toggle($event)"
          aria-label="Profilo utente"
          [attr.aria-haspopup]="'menu'"
          [attr.aria-controls]="'user-menu-popup'"
        >
          <i class="pi pi-user"></i>
        </button>

        <!-- User Dropdown Menu -->
        <p-menu #userMenu [model]="userMenuItems" [popup]="true" id="user-menu-popup">
          <ng-template #start>
            <div class="flex flex-column gap-2 p-3 bg-highlight-subtle">
              <!-- Nome e Cognome -->
              <span class="font-bold text-900">{{ userName() || 'Utente' }}</span>
              
              <!-- Email -->
              <small class="text-secondary">{{ userEmail() || 'Email non disponibile' }}</small>

              <!-- Professional ID -->
              @if (professionalId && isProfessionalOrCollaborator()) {
                <div class="flex align-items-center gap-2 mt-2">
                  <!-- WCAG 1.1.1: Decorative icon, hidden from screen readers -->
                  <i class="pi pi-id-card text-primary" aria-hidden="true"></i>
                  <small class="text-600 font-medium">ID: {{ professionalId }}</small>
                </div>
              }

              <!-- Professional Type -->
              @if (professionalType() && isProfessionalOrCollaborator()) {
                <div class="flex align-items-center gap-2">
                  <!-- WCAG 1.1.1: Decorative icon, hidden from screen readers -->
                  <i class="pi pi-briefcase text-primary" aria-hidden="true"></i>
                  <small class="text-600 font-medium">{{ professionalType() }}</small>
                </div>
              }

              <!-- Role Badges -->
              @if (userRoles().length > 0) {
                <div class="flex gap-1 flex-wrap mt-2">
                  @for (role of userRoles(); track role) {
                    @if (getRoleBadge(role); as badge) {
                      <p-tag [value]="badge.label" [severity]="$any(badge.severity)" size="small"></p-tag>
                    }
                  }
                </div>
              }
            </div>
          </ng-template>
        </p-menu>
      </div>
    }
  </div>

  <!-- Mobile Navigation Overlay -->
  @if (!isLoginPage() && isMobileMenuOpen()) {
    <div class="mobile-nav-overlay"
         (click)="closeMobileMenu()"
         (keydown.escape)="closeMobileMenu()"
         role="dialog"
         aria-modal="true"
         aria-label="Menu di navigazione">
      <nav class="mobile-nav-menu" role="navigation" aria-label="Menu mobile" (click)="$event.stopPropagation()">
        @for (item of navItems; track item.label) {
          @if (item.children?.length) {
            <span class="mobile-nav-group-label">{{ item.label }}</span>
            @for (child of item.children; track child.label) {
              <button
                class="mobile-nav-item mobile-nav-child"
                [class.active]="isChildRouteActive(child.route)"
                (click)="navigateTo(child.route); closeMobileMenu()"
              >
                @if (child.icon) { <i [class]="child.icon + ' mr-2'" aria-hidden="true"></i> }
                <span>{{ child.label }}</span>
              </button>
            }
          } @else {
            <button
              class="mobile-nav-item"
              [class.active]="item.active"
              (click)="navigateTo(item.route); closeMobileMenu()"
              [attr.aria-current]="item.active ? 'page' : null"
            >
              <span>{{ item.label }}</span>
            </button>
          }
        }
      </nav>
    </div>
  }
</header>

<!-- WCAG 4.1.3: Toast for notifications with ARIA -->
<p-toast role="status" aria-live="polite" aria-atomic="true"></p-toast>
